// Admin page (/admin, behind Cloudflare Access): the runner's quotas, pacing, spend and
// settings, refreshed every 15 s. The Worker answers only a browser signed in as an admin.

import type { AdminStatusResponse } from '../src/api/contract';
import type { AdminRunnerStatus, Settings } from '../src/types';
import { ApiError, api, describeError, matchUrl, modelUrl } from './api';
import { emptyState, errorState, everyWhileVisible, fact, h, link, requireEl, show, type Child } from './dom';
import { fmtCountdown, fmtFixed, fmtInt, fmtUtc, modeLabel, parseDate, phaseText, setupLabel } from './format';

const REFRESH_MS = 15_000;

export async function initAdmin(): Promise<void> {
  const viewerEl = requireEl('admin-viewer');
  const statusBox = requireEl('admin-status');
  const nowBox = requireEl('admin-now');
  const settingsBox = requireEl('admin-settings');
  let countdowns: { el: HTMLElement; at: string }[] = [];
  let loaded = false;
  const names = new Map<string, string>();
  void api
    .models()
    .then((res) => {
      for (const m of Array.isArray(res.models) ? res.models : []) if (m && typeof m.id === 'string') names.set(m.id, m.displayName || m.id);
    })
    .catch(() => undefined);

  window.setInterval(() => {
    if (!document.hidden) for (const c of countdowns) c.el.textContent = fmtCountdown(c.at);
  }, 1000);

  const refresh = async (): Promise<void> => {
    try {
      const st = await api.adminStatus();
      loaded = true;
      viewerEl.replaceChildren('Signed in as ', h('strong', null, st.viewer), ' · ', link('/cdn-cgi/access/logout', 'Sign out'));
      countdowns = [];
      show(statusBox, statusGrid(st.runner, st.settings, (id) => names.get(id) ?? id, (el, at) => countdowns.push({ el, at })));
      show(nowBox, nowPanel(st, (id) => names.get(id) ?? id));
      show(settingsBox, settingsList(st.settings));
    } catch (err) {
      // After the first load a failed refresh keeps the last figures and says so.
      if (loaded) {
        viewerEl.textContent = `Couldn't refresh: ${describeError(err)}`;
        return;
      }
      show(statusBox, failure(err));
      show(nowBox);
      show(settingsBox);
    }
  };
  everyWhileVisible(REFRESH_MS, () => void refresh());
  await refresh();
}

function failure(err: unknown): HTMLElement {
  if (err instanceof ApiError && err.status === 503 && /not set up/i.test(err.message)) {
    return emptyState('The admin login is not set up yet', 'Add the Cloudflare Access team domain and application AUD tag to the Worker (ACCESS_TEAM_DOMAIN, ACCESS_AUD).');
  }
  if (err instanceof ApiError && err.status === 403) {
    return h(
      'div',
      { class: 'state state--empty' },
      h('p', { class: 'state-title' }, 'This account is not an admin'),
      h('p', null, link('/cdn-cgi/access/logout', 'Sign out'), ' and sign in with an admin email.'),
    );
  }
  // An expired Access session answers with a redirect to the login page, which fetch can't follow.
  if (err instanceof ApiError && err.status === 0) {
    return errorState('Your login may have expired. Reload the page to sign in again.', () => location.reload());
  }
  return errorState(describeError(err), () => location.reload());
}

function item(label: string, ...value: Child[]): HTMLElement {
  return h('div', { class: 'status-item' }, h('span', { class: 'status-label' }, label), ...value);
}

function statusGrid(r: AdminRunnerStatus, s: Settings, name: (id: string) => string, tick: (el: HTMLElement, at: string) => void): HTMLElement {
  const items: Child[] = [
    item('Runner', h('span', { class: `phase phase--${r.phase}` }, phaseText(r.phase)), r.detail ? h('span', { class: 'status-detail' }, r.detail) : null),
  ];
  // One bar per provider with keys; older status payloads only carry OpenRouter's counter.
  const providers =
    Array.isArray(r.capacity) && r.capacity.length > 0
      ? r.capacity.map((c) => ({ label: `${providerLabel(c.provider)} requests today`, used: c.used, limit: c.budget }))
      : [{ label: 'Free requests today', used: r.freeRequestsUsed, limit: r.freeRequestsLimit }];
  providers.forEach((p, i) => items.push(...quotaItem(p.label, p.used, p.limit, `quota-label-${i}`)));
  for (const c of Array.isArray(r.capacity) ? r.capacity : []) {
    if (c.usdCap > 0 || c.usdSpent > 0) items.push(item(`${providerLabel(c.provider)} spend today`, h('span', { class: 'mono' }, `${usd(c.usdSpent)}${c.usdCap > 0 ? ` / ${usd(c.usdCap)}` : ''}`)));
  }
  for (const m of Array.isArray(r.modelCapacity) ? r.modelCapacity : []) {
    const blocked = parseDate(m.blockedUntil);
    if (!blocked || blocked.getTime() <= Date.now()) continue;
    const el = h('span', { class: 'mono', attrs: { title: fmtUtc(m.blockedUntil) } }, fmtCountdown(m.blockedUntil));
    tick(el, m.blockedUntil ?? '');
    items.push(item(`${name(m.modelId)} resting`, h('span', null, 'back ', el)));
  }
  const paced = parseDate(r.pacedUntil);
  if (paced && paced.getTime() > Date.now() && r.pacedUntil) {
    const el = h('span', { class: 'mono' }, fmtCountdown(r.pacedUntil));
    tick(el, r.pacedUntil);
    items.push(item('Pacing', h('span', { attrs: { title: "Calls are spread so today's requests last the whole day" } }, 'next call ', el)));
  }
  items.push(item('Judge spend today', h('span', { class: 'mono' }, usd(r.jevSpentUsd))));
  const jevPaused = parseDate(r.jevPausedUntil);
  if (jevPaused && jevPaused.getTime() > Date.now() && r.jevPausedUntil) {
    const el = h('span', { class: 'mono' }, fmtCountdown(r.jevPausedUntil));
    tick(el, r.jevPausedUntil);
    items.push(item('Jev judge skipped', h('span', null, 'back ', el)));
  }
  const next = parseDate(r.nextActionAt);
  if (next && r.nextActionAt && (next.getTime() > Date.now() || r.phase === 'running')) {
    const el = h('span', { class: 'mono', attrs: { title: fmtUtc(r.nextActionAt) } }, fmtCountdown(r.nextActionAt));
    tick(el, r.nextActionAt);
    items.push(item('Next step', el));
  }
  items.push(item('Modes on', h('span', null, list(s.modesEnabled, modeLabel))));
  return h('div', { class: 'status-grid' }, ...items);
}

function nowPanel(st: AdminStatusResponse, name: (id: string) => string): HTMLElement {
  const cur = st.current && st.current.status === 'running' ? st.current : null;
  return h(
    'div',
    null,
    cur
      ? h(
          'p',
          null,
          'Playing now: ',
          link(modelUrl(cur.modelA), cur.modelAName || cur.modelA, 'atk'),
          h('span', { class: 'vs' }, ' vs '),
          link(modelUrl(cur.modelB), cur.modelBName || cur.modelB, 'def'),
          ' · ',
          link('/live', 'watch'),
          ' · ',
          link(matchUrl(cur.id), 'match page'),
        )
      : h('p', { class: 'muted' }, 'No match running.'),
    st.upNext.length
      ? h(
          'ol',
          { class: 'upnext' },
          ...st.upNext.map((q) =>
            h(
              'li',
              null,
              link(modelUrl(q.modelA), name(q.modelA), 'atk'),
              h('span', { class: 'vs' }, ' vs '),
              link(modelUrl(q.modelB), name(q.modelB), 'def'),
              h('span', { class: 'tag' }, setupLabel(q.setup)),
              h('span', { class: 'tag' }, modeLabel(q.mode)),
            ),
          ),
        )
      : h('p', { class: 'muted' }, 'Nothing queued.'),
  );
}

function settingsList(s: Settings): HTMLElement {
  return h(
    'dl',
    { class: 'facts' },
    fact('Paused', s.paused ? 'Yes' : 'No'),
    fact('Pacing', s.pacing === 'spread' ? `Spread over ${fmtInt(s.spreadHours)} h from 00:00 UTC` : 'As fast as limits allow'),
    fact('Daily call target', s.dailyCallTarget === null ? 'All capacity' : fmtInt(s.dailyCallTarget)),
    fact('Reserve to start a match', `${fmtInt(s.matchReserveCalls)} calls`),
    fact('Setups', list(s.setupsEnabled, setupLabel)),
    fact('Modes', list(s.modesEnabled, modeLabel)),
    fact('Runs per pairing', fmtInt(s.runsPerPairing)),
    fact('Turn cap', fmtInt(s.maxTurns)),
    fact('Temperature', fmtFixed(s.temperature, 2)),
  );
}

function list<T extends string>(values: readonly T[] | undefined, label: (v: T) => string): string {
  return Array.isArray(values) && values.length ? values.map(label).join(', ') : '—';
}

function usd(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
}

const PROVIDER_LABELS: Record<string, string> = {
  openrouter: 'OpenRouter',
  openai: 'OpenAI',
  groq: 'Groq',
  cerebras: 'Cerebras',
  gemini: 'Gemini',
  mistral: 'Mistral',
  together: 'Together',
  deepseek: 'DeepSeek',
  xai: 'xAI',
  fireworks: 'Fireworks',
  abliteration: 'Abliteration',
  custom: 'Custom API',
};

const providerLabel = (id: string): string => PROVIDER_LABELS[id] ?? id;

/** A usage bar against today's budget, or just the count when there's no request budget (paid keys). */
function quotaItem(label: string, used: number | null | undefined, limit: number | null | undefined, id: string): Child[] {
  if (typeof used !== 'number') return [];
  if (typeof limit !== 'number' || limit <= 0) return [item(label, h('span', { class: 'mono' }, fmtInt(used)))];
  const pct = Math.min(100, Math.max(0, (used / limit) * 100));
  const fill = h('span', { class: 'quota-fill' });
  fill.style.width = `${pct.toFixed(1)}%`;
  return [
    h(
      'div',
      { class: 'status-item' },
      h('span', { class: 'status-label', attrs: { id } }, label),
      h(
        'span',
        {
          class: `quota${pct >= 90 ? ' quota--high' : ''}`,
          attrs: {
            role: 'progressbar',
            'aria-labelledby': id,
            'aria-valuemin': 0,
            'aria-valuemax': limit,
            // Failed calls count too, so "used" can pass the limit; keep the value in range.
            'aria-valuenow': Math.min(Math.max(used, 0), limit),
            'aria-valuetext': `${fmtInt(used)} of ${fmtInt(limit)}`,
          },
        },
        fill,
      ),
      h('span', { class: 'mono' }, `${fmtInt(used)} / ${fmtInt(limit)}`),
    ),
  ];
}
